import React, { useCallback, useEffect, useState } from 'react';
import { useField } from 'formik';
import ReactSelect from 'react-select';
import { IFormItem } from '../form-item.type';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import { FormErrorMsg } from '../form-error-msg';
import clsx from 'clsx';

type SelectOption = {
  label: string | JSX.Element;
  value: any;
  searchableLabel?: string;
  isDisabled?: boolean;
  isInvisible?: boolean;
  // For indent, default no indent
  indentLevel?: number;
  // Default: space
  indentType?: string;
};

export type SelectOptions = SelectOption[];

interface ISelect extends IFormItem {
  /**
   * Default: []
   */
  options?: SelectOptions;

  /**
   * Default: false.
   */
  hasSearch?: boolean;

  placeholder?: string;

  readOnly?: boolean;

  /**
   * Default: false.
   */
  isMulti?: boolean;

  isSelectAll?: boolean;

  noOptionMsg?: string;

  className?: string;
  numberType?: number;
}

const indentSpace = {
  1: '  ',
  2: '     ',
  3: '        ',
  4: '           ',
  5: '              ',
  6: '                 ',
  7: '                    ',
  8: '                      ',
  9: '                         ',
  10: '                           ',
  11: '                              ',
  12: '                                 ',
};

export function Select({
  name,
  options,
  disabled,
  placeholder,
  hasSearch,
  readOnly,
  isMulti,
  isSelectAll,
  noOptionMsg = 'No options',
  className,
  onChange,
  numberType,
}: ISelect) {
  const [field, , helper] = useField(name);
  const [inUsedOptions, setInUsedOptions] = useState<SelectOptions>(options || []);

  useEffect(() => {
    setInUsedOptions(options || []);
  }, [options]);

  const handleItemSelect = useCallback(
    (item) => {
      helper.setTouched(true);

      // Multiple select
      if (Array.isArray(item)) {
        const value = item.find((optionVal) => optionVal.value === 'all')
          ? inUsedOptions.filter((item) => item.value !== 'all').map((option) => option.value)
          : item.map((data) => data.value);

        helper.setValue(value);
        onChange && onChange(value);
      } else {
        helper.setValue(item.value);
        onChange && onChange(item.value);
      }
    },
    [helper, inUsedOptions, onChange]
  );

  const selectedValue = inUsedOptions?.filter(
    (opt) => opt.value === field.value || (Array.isArray(field.value) && field.value?.includes(opt.value))
  );

  const customFilter = useCallback((option: FilterOptionOption<SelectOption>, inputValue: string) => {
    if (option.data.isDisabled) return false;

    return (option.data.searchableLabel || option.label)?.toLocaleLowerCase().includes(inputValue?.toLocaleLowerCase());
  }, []);

  const optionsActual = isSelectAll ? [{ label: 'Select All', value: 'all' }, ...inUsedOptions] : inUsedOptions;

  return (
    <>
      <ReactSelect
        filterOption={customFilter}
        // classNamePrefix='form-control-select'
        className={clsx('form-control-wrap-select', className)}
        name={field.name}
        value={selectedValue}
        onChange={handleItemSelect}
        placeholder={placeholder}
        options={optionsActual
          .filter((dataItem) => (numberType ? dataItem.indentLevel <= numberType : dataItem))
          .map((item) => {
            const newItem = { ...item };

            if (newItem.indentLevel) {
              let indentSp: string = indentSpace[newItem.indentLevel];
              let indentSp1 = '';

              if (newItem.indentType) indentSp1 = newItem.indentType;

              newItem.label = `${indentSp}${indentSp1}${newItem.label}`;
            }

            return newItem;
          })}
        isSearchable={hasSearch && !readOnly}
        menuIsOpen={readOnly ? false : undefined}
        isDisabled={disabled}
        isOptionDisabled={(option) => option.isInvisible}
        isMulti={isMulti}
        noOptionsMessage={() => <>{noOptionMsg}</>}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: '#dc3545',
          },
          spacing: {
            ...theme.spacing,
          },
        })}
        styles={{
          option: (base) => ({
            ...base,
            minHeight: 36,
          }),
        }}
      />

      <FormErrorMsg name={name} />
    </>
  );
}
